import { useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import classNames from 'classnames'
import { noop } from 'lodash'
import { faPencil } from '@fortawesome/pro-regular-svg-icons'
import { Event, useUploadEventThumbMutation } from 'graphql/generated'
import Button from 'components/Button'
import { twMerge } from 'tailwind-merge'

interface IProps {
  event: Pick<Event, 'id' | 'canEdit' | 'thumb'>
}

const ImageWidth = 528
const ImageHeight = 266 //50%

function EventThumbnail({ event }: IProps) {
  // ----- File
  const fileInputRef = useRef<HTMLInputElement>(null!)
  const [uploadedFile, setUploadedFile] = useState<Maybe<IFile['file']>>()
  const [uploadFile] = useUploadEventThumbMutation()

  const onFileSelect = async (origFiles: FileList | null) => {
    if (!origFiles || !origFiles.length) {
      return
    }

    const file = origFiles[0]
    setUploadedFile(file)
    await uploadFile({
      variables: {
        eventId: event.id,
        file: file
      }
    })
  }
  const previewUrl = useMemo(
    () => (uploadedFile ? URL.createObjectURL(uploadedFile) : event.thumb),
    [event.thumb, uploadedFile]
  )

  // ---- permission
  const canEdit = event.canEdit
  if (!canEdit && !previewUrl?.length) {
    return null
  }

  return (
    <div
      className={classNames(canEdit && 'cursor-pointer')}
      onClick={canEdit ? () => fileInputRef.current?.click() : noop}
    >
      {previewUrl ? (
        <div className="w-full aspect-w2/h1 relative max-h-[311px] overflow-hidden group">
          <Image
            alt="Event thumbnail"
            src={previewUrl}
            className={twMerge('object-cover w-full h-full object-center')}
            fill
          />

          {canEdit && (
            <Button
              color="gray"
              icon={faPencil}
              label="Change"
              className="absolute bottom-2 right-2"
            />
          )}
        </div>
      ) : (
        <div
          className={
            'bg-[#E7EFFD] min-h-[12rem] md:min-h-[20rem] flex justify-center items-center px-2'
          }
        >
          <Image
            alt="Event thumbnail placeholder"
            src="/static/events/robot_placeholder.png"
            width={110}
            height={68}
          />
          <span className="text-sm font-light text-gray-400 mt-2 text-center">
            Click to upload a thumbnail for the event
            <br />
            {`(Preferred ${ImageWidth}px x ${ImageHeight}px ratio)`}
          </span>
        </div>
      )}

      <input
        hidden
        multiple
        type="file"
        className="hidden invisible"
        accept="image/*"
        ref={fileInputRef}
        onChange={canEdit ? e => onFileSelect(e.target.files) : noop}
      />
    </div>
  )
}

export default EventThumbnail
